import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fieldErrorId,
  focusFirstInvalidField,
  hasValidationErrors,
  validateFieldValue,
  validateFormValues,
} from "../utils/helpers/formValidation";

const useFormValidation = ({ values, schema, formId = "akas-form", focusRoot } = {}) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const valuesRef = useRef(values);
  const schemaRef = useRef(schema);

  valuesRef.current = values;
  schemaRef.current = schema;

  useEffect(() => {
    setErrors((current) => {
      const next = { ...current };
      let changed = false;
      Object.keys(current).forEach((field) => {
        const message = validateFieldValue(values?.[field], schema?.[field], values);
        if (message !== current[field]) {
          changed = true;
          if (message) next[field] = message;
          else delete next[field];
        }
      });
      return changed ? next : current;
    });
  }, [schema, values]);

  const validateField = useCallback((field, { force = false } = {}) => {
    if (!hasSubmitted && !force) return true;

    const message = validateFieldValue(valuesRef.current?.[field], schemaRef.current?.[field], valuesRef.current);
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current) => {
      const next = { ...current };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
    return !message;
  }, [hasSubmitted]);

  const validateAll = useCallback(() => {
    setHasSubmitted(true);
    const next = validateFormValues(valuesRef.current, schemaRef.current);
    setErrors(next);
    setTouched(Object.keys(schemaRef.current || {}).reduce((result, field) => ({ ...result, [field]: true }), {}));

    if (hasValidationErrors(next)) {
      focusFirstInvalidField(next, focusRoot);
      return false;
    }
    return true;
  }, [focusRoot]);

  const resetValidation = useCallback(() => {
    setErrors({});
    setTouched({});
    setHasSubmitted(false);
  }, []);

  const setExternalErrors = useCallback((nextErrors = {}) => {
    setHasSubmitted(true);
    setErrors(nextErrors);
    setTouched((current) => ({
      ...current,
      ...Object.keys(nextErrors).reduce((result, field) => ({ ...result, [field]: true }), {}),
    }));
    if (hasValidationErrors(nextErrors)) focusFirstInvalidField(nextErrors, focusRoot);
  }, [focusRoot]);

  const fieldProps = useCallback(
    (field) => ({
      name: field,
      onBlur: () => validateField(field),
      "aria-invalid": Boolean(errors[field]) || undefined,
      "aria-describedby": errors[field] ? fieldErrorId(formId, field) : undefined,
    }),
    [errors, formId, validateField],
  );

  const inputClass = useCallback(
    (field, baseClass = "form-control") => `${baseClass}${errors[field] ? " is-invalid" : ""}`,
    [errors],
  );

  const api = useMemo(
    () => ({
      errors,
      touched,
      hasSubmitted,
      validateAll,
      validateField,
      resetValidation,
      setExternalErrors,
      fieldProps,
      inputClass,
      isValid: !hasValidationErrors(validateFormValues(values, schema)),
    }),
    [errors, fieldProps, hasSubmitted, inputClass, resetValidation, schema, setExternalErrors, touched, validateAll, validateField, values],
  );

  return api;
};

export default useFormValidation;
